import React, { Component } from 'react';
import Form from 'react-bootstrap/Form';

import Select from 'components/controls/select';
import CellWorkflowView, { CELL_WORKFLOW_MODE_LABELS } from './cellWorkflowView';
import WorkflowGraph from './workflowGraph';
import { isStructureEditable } from './cellRules';
import { workflowBody, workflowAlias, workflowSelection } from '../../domain/cellWorkflow';
import { upsertCell } from '../../domain/model';
import { CELL_WORKFLOW_MODES } from '../../domain/schema';

/**
 * CellWorkflowSection — which workflow a cell runs, choosable or frozen, and the rule for which.
 *
 * Extracted for the reason CellDetailsSection was, and the rule is the same one: a cell is editable
 * while it is a draft AND the revision and session allow it. It lived in the dock panel, so the
 * cell's own screen — one click away, reading as the same product — could only ever show what had
 * been chosen.
 *
 * The mode and the workflow travel TOGETHER on every write. A mode change without its resolved
 * workflow, or the reverse, leaves the two inconsistent server-side, and only one of them decides
 * what actually runs.
 *
 * `specify_workflow` is the only mode that keeps a workflow; both inherit modes clear it, because
 * they are resolved at dispatch from the mission or the previous cell. What is written is the
 * BODY, resolved from the palette at pick time — a bare name would reintroduce the reference
 * semantics the embedded copy exists to avoid, and the palette moves on while the cell does not.
 *
 * Props:
 *   cell, missionId, client   — what to write and where
 *   templates                 — store.templates; `workflows` is the palette to choose from
 *   onApplyLocal              — (updater) => void; reflect the write without a reload
 *   canEdit                   — false on a committed revision, a closed session, or no write access
 */
const NONE_OPTION = { value: '', label: '— none —' };

class CellWorkflowSection extends Component {
  _workflows() {
    return (this.props.templates && this.props.templates.workflows) || [];
  }

  // The one write path: the pair, then the local reflection.
  async _save(mode, cellWorkflow) {
    const { cell, missionId, client, onApplyLocal } = this.props;
    if (!cell || !client) return;
    const changed = { cell_workflow_mode: mode, cell_workflow: cellWorkflow };
    try {
      await client.setCellProps(missionId, cell.element_id, changed);
      if (onApplyLocal) {
        onApplyLocal(s => upsertCell(s, missionId, {
          ...cell, props: { ...(cell.props || {}), ...changed },
        }));
      }
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveMode(mode) {
    const { cell } = this.props;
    const next = mode || '';
    if (next === ((cell.props || {}).cell_workflow_mode || '')) return;
    const keep = next === 'specify_workflow' ? ((cell.props || {}).cell_workflow || null) : null;
    await this._save(next || null, keep);
  }

  async _saveWorkflow(value) {
    const { cell } = this.props;
    const selected = value || '';
    if (selected === workflowAlias(cell && cell.props && cell.props.cell_workflow)) return;
    await this._save('specify_workflow', workflowSelection(selected, this._workflows()));
  }

  render() {
    const { cell, canEdit } = this.props;
    const workflows = this._workflows();
    if (!isStructureEditable(cell) || canEdit === false) {
      // The frozen view: once a cell has started there is no choice left to describe, so the mode,
      // the help text and the pickers all go and what it IS running is the whole of it.
      return <CellWorkflowView cell={cell} workflows={workflows} />;
    }

    const props = cell.props || {};
    // Never the body itself: passing it to JSX is what took the whole widget down once with
    // "Objects are not valid as a React child" (see domain/cellWorkflow.js).
    const key = workflowAlias(props.cell_workflow);
    // Older cells predate cell_workflow_mode: a bare workflow reads as specify_workflow.
    const mode = props.cell_workflow_mode || (key ? 'specify_workflow' : null);

    const modeOptions = CELL_WORKFLOW_MODES.map(
      value => ({ value, label: CELL_WORKFLOW_MODE_LABELS[value] || value }));
    const workflowOptions = [
      NONE_OPTION,
      ...workflows.map(entry => entry.alias).filter(Boolean)
        .map(alias => ({ value: alias, label: alias })),
    ];

    return (
      <>
        <Form.Group>
          <Select
            options={modeOptions}
            value={modeOptions.find(option => option.value === mode) || null}
            onChange={option => this._saveMode(option ? option.value : '')}
            menuPortalTarget={document.body}
            placeholder="Choose a mode…"
          />
          <Form.Text muted>How the workflow is chosen.</Form.Text>
        </Form.Group>
        {mode === 'specify_workflow' && (
          <Form.Group>
            <Select
              options={workflowOptions}
              value={workflowOptions.find(option => option.value === key) || NONE_OPTION}
              onChange={option => this._saveWorkflow(option ? option.value : '')}
              menuPortalTarget={document.body}
              placeholder="Choose a workflow…"
            />
            {/* The chosen workflow's step graph, right under the picker. */}
            {workflowBody(props.cell_workflow, workflows)
              && <WorkflowGraph workflow={workflowBody(props.cell_workflow, workflows)} />}
            <Form.Text muted>The steps this cell runs.</Form.Text>
          </Form.Group>
        )}
      </>
    );
  }
}

export default CellWorkflowSection;
